import { gql } from "generated-graphql";

export const EPISODIC_EPA_FILING_FRAGMENT = gql(`
  fragment EpisodicEpaFilingFields on EpisodicEpaFiling {
    handlerId
    activityLocation
    seqNumber
    receiveDate
    eventType
    startDate
    endDate
    projectTypes
    otherProjectDescription
    wastes {
      description
      estimatedQuantity
      wasteCodes
      stateWasteCodes
    }
  }
`);

/**
 * Shared row shape for the episodic events ledger. `submissions` is the
 * EPA-notification filing history (newest first); `containers` is the set
 * attached now, which the finalize and lapse controls key on;
 * `containerHistory` is every container on the event's record, which the page
 * displays; `relief` is the advisory on whether the event was needed (it
 * changes no behavior).
 */
export const EPISODIC_EVENT_ITEM_FRAGMENT = gql(`
  fragment EpisodicEventItem on WasteEpisodicEvent {
    id
    facilityId
    type
    status
    reason
    startDate
    endDate
    accumulationDeadline
    relief {
      isAvailable
      reasonKind
      reason
      startMonthGeneratorStatus
    }
    notices {
      id
      kind
      noticeDate
      method
      note
      document {
        id
        title
        fileExtension
      }
      epaFiling {
        ...EpisodicEpaFilingFields
      }
    }
    submissions {
      id
      title
      status
      dueDate
      filedAt
    }
    containers {
      id
      status
      amount
      amountUnit
      archivedAt
    }
    containerHistory {
      isAttached
      attachedAt
      firstFillAt
      addedLb
      shippedLb
      lastRemoval
      container {
        id
        containerLabel
        status
        accumulationArea {
          id
          name
          type
        }
        wasteStream {
          id
          name
        }
      }
    }
  }
`);

/** Facility-scoped year ledger — filterable by status, paginated. */
export const FACILITY_EPISODIC_EVENTS_QUERY = gql(`
  query FacilityEpisodicEvents(
    $facilityId: ID!
    $status: WasteEpisodicEventStatus
    $page: Int
    $pageSize: Int
  ) {
    facility(id: $facilityId) {
      id
      name
      generationDrivenStatus
      episodicEvents(status: $status, page: $page, pageSize: $pageSize) {
        items {
          ...EpisodicEventItem
        }
        count
      }
    }
  }
`);

export const EPISODIC_EVENT_VALIDATOR_QUERY = gql(`
  query EpisodicEventInputValidator($input: EpisodicEventInput!, $currentEventId: ID) {
    episodicEventInputValidator(input: $input, currentEventId: $currentEventId) {
      ...issue
    }
  }
`);

export const CREATE_EPISODIC_EVENT_MUTATION = gql(`
  mutation CreateEpisodicEvent($input: CreateEpisodicEventInput!) {
    createEpisodicEvent(input: $input) {
      ...EpisodicEventItem
    }
  }
`);

export const UPDATE_EPISODIC_EVENT_MUTATION = gql(`
  mutation UpdateEpisodicEvent($input: UpdateEpisodicEventInput!) {
    updateEpisodicEvent(input: $input)
  }
`);

export const FINALIZE_EPISODIC_EVENT_MUTATION = gql(`
  mutation FinalizeEpisodicEvent($id: ID!) {
    finalizeEpisodicEvent(id: $id)
  }
`);

export const REOPEN_EPISODIC_EVENT_MUTATION = gql(`
  mutation ReopenEpisodicEvent($id: ID!) {
    reopenEpisodicEvent(id: $id)
  }
`);

export const DELETE_EPISODIC_EVENT_MUTATION = gql(`
  mutation DeleteEpisodicEvent($id: ID!) {
    deleteEpisodicEvent(id: $id)
  }
`);

/** Attaches a whole selection in one transaction — see the schema field: a
 * container the guard refuses leaves none of them attached. */
export const SET_CONTAINERS_EPISODIC_EVENT_MUTATION = gql(`
  mutation SetContainersEpisodicEvent($containerIds: [ID!]!, $episodicEventId: ID) {
    setContainersEpisodicEvent(containerIds: $containerIds, episodicEventId: $episodicEventId)
  }
`);

export const SET_CONTAINER_EPISODIC_EVENT_MUTATION = gql(`
  mutation SetContainerEpisodicEvent(
    $containerId: ID!
    $episodicEventId: ID
    $detachFromEpisodicEventId: ID
  ) {
    setContainerEpisodicEvent(
      containerId: $containerId
      episodicEventId: $episodicEventId
      detachFromEpisodicEventId: $detachFromEpisodicEventId
    )
  }
`);

/** The attach picker's options. The server decides attachability (see the
 * schema field), so the picker cannot drift from the attach guard. */
export const EPISODIC_ATTACHABLE_CONTAINERS_QUERY = gql(`
  query EpisodicAttachableContainers($eventId: ID!) {
    episodicEvent(id: $eventId) {
      id
      relief {
        isAvailable
        reasonKind
        reason
      }
      attachableContainers {
        id
        containerLabel
        status
        accumulationArea {
          id
          name
        }
      }
    }
  }
`);

export const CREATE_EPISODIC_EVENT_SUBMISSION_MUTATION = gql(`
  mutation CreateEpisodicEventSubmission($eventId: ID!) {
    createEpisodicEventSubmission(eventId: $eventId)
  }
`);

export const UPSERT_EPISODIC_EVENT_NOTICE_MUTATION = gql(`
  mutation UpsertEpisodicEventNotice($input: UpsertEpisodicEventNoticeInput!) {
    upsertEpisodicEventNotice(input: $input) {
      id
      kind
      noticeDate
      method
      note
      document {
        id
        title
        fileExtension
      }
      epaFiling {
        ...EpisodicEpaFilingFields
      }
    }
  }
`);

export const DELETE_EPISODIC_EVENT_NOTICE_MUTATION = gql(`
  mutation DeleteEpisodicEventNotice($eventId: ID!, $kind: WasteEpisodicEventNoticeKind!) {
    deleteEpisodicEventNotice(eventId: $eventId, kind: $kind)
  }
`);

export const EPISODIC_EPA_FILING_CANDIDATES_QUERY = gql(`
  query EpisodicEpaFilingCandidates(
    $facilityId: ID!
    $type: WasteEpisodicEventType!
    $startDate: Date!
    $excludeEventId: ID
  ) {
    episodicEpaFilingCandidates(
      facilityId: $facilityId
      type: $type
      startDate: $startDate
      excludeEventId: $excludeEventId
    ) {
      typeMatches
      filing {
        ...EpisodicEpaFilingFields
      }
    }
  }
`);
